import React from 'react'
import { useApiQuery } from '../../../utils/api'
import { Loading, Error } from '../../Elements/Indicators'
import { useParams } from 'react-router-dom'
import { PageHeader } from '../../Elements/PageHeader'
import { PSmallFaded } from '../../Elements/Type'
import { UserProgressCard } from '../../Elements/Cards'
import {
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/20/solid'
import { CosmosAnalysisUserStats, JiraUserWithRole } from '@jirax/common'
import { IssuesPanel, Category } from '../../Elements/JirraIssue'

// Update the AnalysisUser interface to properly handle the property mapping
// CosmosAnalysisUserStats uses id and name, while we need to map to accountId and displayName
interface AnalysisUser extends CosmosAnalysisUserStats {
  // Add JiraUser properties that will be used by UserProgressCard
  accountId?: string // Directly equals id from analysis (see mapping below)
  displayName?: string // Will be mapped from name
  avatarUrl?: string // Will be added from JiraUser
  role?: string // Role from Jira project roles
  emailAddress?: string // allow safe access without casting
}

interface AnalysisChanges {
  addedIssues: unknown[]
  removedIssues: unknown[]
  changedIssues: unknown[]
  pointsAdded: number
  pointsRemoved: number
  pointsChanged: number
  pointsUnresolved: number
  pointsDelta: number
  bugsAdded: number
  bugsRemoved: number
}

interface Analysis {
  startPoints: number
  resolvedPoints: number
  startBugs: number
  resolvedBugs: number
  changes: AnalysisChanges
  byUser: AnalysisUser[]
}

interface Sprint {
  id: number
  name: string
  state: string
  startDate: string
  endDate: string
  users: JiraUserWithRole[] // Updated to use JiraUserWithRole with role information
}

interface RouteParams extends Record<string, string | undefined> {
  projKey: string
  boardId: string
  sprintId: string
}

interface UsersProps {
  users: Sprint['users'] // Now expects users with role information
  analysis: AnalysisUser[]
  projKey: string
  boardId: string
  sprintId: string
}

/* ------------------------------------------------------------
 * Added-By endpoint response
 * ---------------------------------------------------------- */
interface AddedByResponse {
  key: string
  addedByName: string
  addedById: string
}

const Users: React.FC<UsersProps> = ({ users, analysis, projKey, boardId, sprintId }) => {

  // Merge analysis stats with sprint user metadata (name, avatar, role)
  const enrichedAnalysis: AnalysisUser[] = analysis.map(ua => {
    // 1️⃣  Primary match: analysis.id  ➜  sprintUser.accountId
    let match = users.find(u => u.accountId === ua.id)

    // 2️⃣  Fallback match: analysis.name  ➜  sprintUser.displayName  (case-insensitive)
    if (!match && ua.name) {
      const target = ua.name.trim().toLowerCase()
      match = users.find(
        u => u.displayName && u.displayName.trim().toLowerCase() === target
      )
    }

    // 3️⃣  Extra fallback: some back-ends might send `emailAddress` only
    if (!match && ua.emailAddress) {
      const email = ua.emailAddress.trim().toLowerCase()
      match = users.find(
        u =>
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          (u as unknown as { emailAddress?: string }).emailAddress?.trim().toLowerCase() === email
      )
    }

    if (match) {
      return {
        ...ua,
        accountId: match.accountId,
        displayName: match.displayName,
        avatarUrl: match.avatarUrl,
        role: match.role ?? 'Team Member'
      }
    }

    // Fallback: use name from analysis, no avatar, default role
    console.info(
      `[SprintAnalysis] could not map analysis user '${ua.id}/${ua.name}' to sprint user metadata`
    )
    return {
      ...ua,
      accountId: ua.id,
      displayName: ua.name || 'Unknown User',
      role: 'Team Member'
    }
  })

  // Exclude users who contributed neither points nor bugs
  const filteredAnalysis = enrichedAnalysis.filter(user =>
    user.startPoints > 0 ||
    user.startBugs > 0 ||
    user.resolvedPoints > 0 ||
    user.resolvedBugs > 0
  )

  return (
    <ul
      role='list'
      className='grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
    >
      {filteredAnalysis.map((user, idx) => {
        // build a safe id for keys, links, etc.
        const userId = user.accountId || user.id || `idx-${idx}`

        const uniqueKey = `${userId}-${idx}` // always unique thanks to idx

        const profileLink =
          userId && !userId.startsWith('idx-')
            ? `/projects/${projKey}/boards/${boardId}/sprints/${sprintId}/analysis/user/${userId}`
            : undefined

        return (
          <UserProgressCard
            user={{
              ...user,
              // Use emailAddress as a unique identifier if accountId is missing
              id: user.accountId || user.id || 'unknown',
              // Ensure we have a display name
              name: user.displayName || user.name || 'Unknown User',
              // Map avatarUrl to avatar for backward compatibility
              avatar: user.avatarUrl || '',
              // Use the role from backend or fallback to Team Member
              role: user.role || 'Team Member'
            }}
            key={uniqueKey}
            link={profileLink}
          />
        )
      })}
    </ul>
  )
}

const SprintAnalysis: React.FC = () => {
  const { projKey, boardId, sprintId } = useParams<RouteParams>()

  /* ------------------------------------------------------------------
   * All hooks are declared up-front; execution is gated via `enabled`
   * flags so we never violate the Rules-of-Hooks.
   * ------------------------------------------------------------------ */
  const hasParams = Boolean(projKey && boardId && sprintId)
  const {
    isLoading: isLoadingData,
    error,
    data: sprint
  } = useApiQuery<Sprint>(['sprints', sprintId], { enabled: hasParams })

  const {
    isLoading,
    data: analysis
  } = useApiQuery<Analysis>(['analysis', 'sprints', sprintId], {
    enabled: hasParams && !!sprint
  })

  /* ------------------------------------------------------------
   * Batched "added-by" fetch for all non-starting issues
   * ---------------------------------------------------------- */
  // State for tracking issue keys that need "added by" data
  const [nonStartingIssueKeys, setNonStartingIssueKeys] = React.useState<string[]>([])

  // Join keys for the API call
  const keysParam = nonStartingIssueKeys.join(',')

  // Fetch added-by data for all non-starting issues at once
  const {
    data: addedByData,
    isLoading: isLoadingAddedBy
  } = useApiQuery<AddedByResponse[]>(
    `/sprints/${sprintId}/issues/added-by?keys=${keysParam}`,
    {
      enabled: hasParams && !!sprintId && nonStartingIssueKeys.length > 0,
      // This data is immutable for closed sprints – keep it cached
      staleTime: 24 * 60 * 60 * 1000,   // 24 h
      cacheTime: 48 * 60 * 60 * 1000    // 48 h
    }
  )

  // Create lookup map for O(1) access to added-by data
  const addedByLookup = React.useMemo<Record<string, AddedByResponse>>(
    () => {
      const map: Record<string, AddedByResponse> = {}
      addedByData?.forEach(r => { map[r.key] = r })
      return map
    },
    [addedByData]
  )

  // Extract issue keys from analysis.changes when analysis data is available
  React.useEffect(() => {
    if (analysis) {
      // Only addedIssues are injections — removed and changed issues were present at sprint start
      const keys = analysis.changes.addedIssues
        .map(issue => (issue as { key: string }).key)
        .filter(Boolean)

      setNonStartingIssueKeys(keys)
    }
  }, [analysis])

  /* ------------------------------------------------------------
   * Drawer / slide-out panels (IssuesPanel) state (must be declared
   * before any conditional return to obey the Rules-of-Hooks)
   * ---------------------------------------------------------- */
  const [openPanel, setOpenPanel] =
    React.useState<null | 'bugs' | 'tickets' | 'changes'>(null)

  /* ------------------------------------------------------------------
   * Early exit if the mandatory route params are missing.  The exit is
   * placed *after* the hooks so we do not violate the Rules-of-Hooks.
   * ------------------------------------------------------------------ */
  if (!hasParams) {
    return (
      <Error
        title={'Error Loading Sprint Analysis'}
        message={'Missing required parameters in URL'}
      />
    )
  }

  /* ------------------------------------------------------------------
   * From this point onward `projKey`, `boardId`, and `sprintId`
   * are guaranteed (by the early-return above) to be non-undefined.
   * Use non-null assertions so TypeScript recognises this.
   * ------------------------------------------------------------------ */
  const projKeySafe = projKey!    // eslint-disable-line @typescript-eslint/no-non-null-assertion
  const boardIdSafe = boardId!    // eslint-disable-line @typescript-eslint/no-non-null-assertion
  const sprintIdSafe = sprintId!   // eslint-disable-line @typescript-eslint/no-non-null-assertion

  if (isLoading || isLoadingData) {
    return <Loading message={`Loading sprint ${sprintId} analysis.`} />
  }

  if (error) {
    return (
      <Error title={'Error Loading Sprint Analysis'} message={(error as Error).message} />
    )
  }

  // Add null checks for sprint and analysis data
  if (!sprint || !analysis) {
    return <Error title={'Error Loading Sprint Analysis'} message={'Sprint or analysis data not found'} />
  }

  /* ------------------------------------------------------------
   * Derive metrics for the three supplementary cards
   * ---------------------------------------------------------- */
  // utility to detect Bugs (shared by card counts & drawer filters)
  const isBug = (iss: unknown): iss is { type?: string } =>
    !!(iss as { type?: string }).type && (iss as { type?: string }).type === 'Bug'

  const bugsAdded = analysis.changes.bugsAdded
  const bugsRemoved = analysis.changes.bugsRemoved
  const bugsNet = bugsAdded - bugsRemoved

  const ticketsAdded = analysis.changes.addedIssues.filter(i => !isBug(i)).length
  const ticketsRemoved = analysis.changes.removedIssues.filter(i => !isBug(i)).length
  const ticketsNet = ticketsAdded - ticketsRemoved
  const pointsNet = analysis.changes.pointsAdded - analysis.changes.pointsRemoved

  const estimatesChanged = analysis.changes.changedIssues.length
  const pointsChanged = analysis.changes.pointsChanged

  /* ------------------------------------------------------------
   * Helpers to determine whether each change-card has any data.
   * ---------------------------------------------------------- */
  const hasBugChanges = bugsAdded !== 0 || bugsRemoved !== 0
  const hasTicketChanges = ticketsAdded !== 0 || ticketsRemoved !== 0
  const hasEstimateChanges = estimatesChanged !== 0

  /* ------------------------------------------------------------
   * Drawer / slide-out panels (IssuesPanel) state & helpers
   * ---------------------------------------------------------- */
  /*  We cast to `{ name: string; issues: any[] }[]` so the resulting
   *  value is structurally compatible with `Category[]` expected by
   *  <IssuesPanel>.  This avoids the duplicate-type collision that
   *  caused the earlier TS2719 errors. */

  const bugCategories = [
    { name: 'Added', issues: analysis.changes.addedIssues.filter(isBug) },
    { name: 'Removed', issues: analysis.changes.removedIssues.filter(isBug) }
  ] as Category[]

  const ticketCategories = [
    { name: 'Added', issues: analysis.changes.addedIssues.filter(i => !isBug(i)) },
    { name: 'Removed', issues: analysis.changes.removedIssues.filter(i => !isBug(i)) }
  ] as Category[]

  const changeCategories = [
    { name: 'Changed', issues: analysis.changes.changedIssues }
  ] as Category[]

  const headerConfig = {
    pageTitle: sprint.name,
    backLink: {
      label: 'Back',
      to: '/projects'
    },
    breadcrumb: [
      {
        label: 'Projects',
        to: '/projects'
      },
      {
        label: 'Project Details',
        to: `/projects/${projKeySafe}`
      },
      {
        label: `Sprint ${sprintIdSafe} Analysis`
      }
    ],
    rightComponent: (
      <>
        {sprint.state !== 'active' && (
          <PSmallFaded
            text={`${new Date(sprint.startDate).toLocaleDateString('en-US', {
              weekday: 'short',
              day: 'numeric',
              month: 'short'
            })} to ${new Date(sprint.endDate).toLocaleDateString('en-US', {
              weekday: 'short',
              day: 'numeric',
              month: 'short'
            })}`}
          />
        )}

        {sprint.state === 'active' && (
          <PSmallFaded
            text={`Active, started on ${new Date(sprint.startDate).toLocaleDateString('en-US', {
              weekday: 'short',
              day: 'numeric',
              month: 'short'
            })}`}
          />
        )}
      </>
    )
  }

  return (
    <>
      <PageHeader config={headerConfig} />
      <main className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        {/* Progress bar — same style as SprintUserAnalysis */}
        <div className='mb-4 rounded-lg border border-gray-200 bg-white px-6 py-4 shadow-sm'>
          <div className='flex items-center justify-between'>
            {/* Committed */}
            <div className='flex items-center'>
              <div className='flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100'>
                <span className='text-xl font-bold text-indigo-600'>{analysis.startPoints}</span>
              </div>
              <div className='ml-3'>
                <p className='text-sm font-medium text-gray-900'>Committed</p>
                <p className='text-xs text-gray-500'>Start of sprint</p>
                <p className='mt-0.5 text-xs text-gray-400'>{analysis.startBugs} bug{analysis.startBugs !== 1 ? 's' : ''}</p>
              </div>
            </div>

            {/* Progress bar + change pills */}
            <div className='mx-6 flex-1'>
              {(() => {
                const pct = analysis.startPoints > 0 ? analysis.resolvedPoints / analysis.startPoints : 0
                const colorClass = pct > 0.85 ? 'text-green-600' : pct >= 0.5 ? 'text-orange-500' : 'text-red-600'
                const barClass = pct > 0.99 ? 'bg-green-500' : pct > 0.85 ? 'bg-yellow-500' : pct > 0.5 ? 'bg-orange-500' : 'bg-red-500'
                return (
                  <>
                    <div className={`mb-1 flex items-center justify-center gap-1 ${colorClass}`}>
                      {pct >= 0.85 ? (
                        <CheckIcon className='h-4 w-4' />
                      ) : pct < 0.5 ? (
                        <ExclamationTriangleIcon className='h-4 w-4' />
                      ) : null}
                      <span className='text-lg font-bold'>
                        {Number(pct).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div className='relative h-2.5 rounded-full bg-gray-200'>
                      <div
                        className={`absolute h-2.5 rounded-full transition-all ${barClass}`}
                        style={{ width: `${Math.min(100, pct * 100)}%` }}
                      />
                    </div>
                    {/* Change pills */}
                    <div className='mt-3 grid grid-cols-3 gap-2'>
                      {/* Bugs */}
                      <div className='group relative'>
                        <button
                          disabled={!hasBugChanges}
                          onClick={hasBugChanges ? () => setOpenPanel('bugs') : undefined}
                          className={`w-full rounded-md px-2 py-1.5 text-center transition-colors ${hasBugChanges ? 'cursor-pointer bg-red-50 hover:bg-red-100' : 'cursor-default bg-gray-50'}`}
                        >
                          <p className={`text-sm font-bold ${hasBugChanges ? 'text-red-600' : 'text-gray-400'}`}>
                            {bugsNet >= 0 ? '+' : ''}{bugsNet} Bugs
                          </p>
                          <p className={`text-xs ${hasBugChanges ? 'text-red-400' : 'text-gray-400'}`}>
                            {bugsAdded} Added, {bugsRemoved} Removed
                          </p>
                        </button>
                      </div>
                      {/* Tickets */}
                      <div className='group relative'>
                        <button
                          disabled={!hasTicketChanges}
                          onClick={hasTicketChanges ? () => setOpenPanel('tickets') : undefined}
                          className={`w-full rounded-md px-2 py-1.5 text-center transition-colors ${hasTicketChanges ? 'cursor-pointer bg-sky-50 hover:bg-sky-100' : 'cursor-default bg-gray-50'}`}
                        >
                          <p className={`text-sm font-bold ${hasTicketChanges ? 'text-sky-600' : 'text-gray-400'}`}>
                            {ticketsNet >= 0 ? '+' : ''}{ticketsNet} Tickets ({pointsNet >= 0 ? '+' : ''}{pointsNet} pts)
                          </p>
                          <p className={`text-xs ${hasTicketChanges ? 'text-sky-400' : 'text-gray-400'}`}>
                            {ticketsAdded} Added, {ticketsRemoved} Removed
                          </p>
                        </button>
                      </div>
                      {/* Estimates */}
                      <div className='group relative'>
                        <button
                          disabled={!hasEstimateChanges}
                          onClick={hasEstimateChanges ? () => setOpenPanel('changes') : undefined}
                          className={`w-full rounded-md px-2 py-1.5 text-center transition-colors ${hasEstimateChanges ? 'cursor-pointer bg-orange-50 hover:bg-orange-100' : 'cursor-default bg-gray-50'}`}
                        >
                          <p className={`text-sm font-bold ${hasEstimateChanges ? 'text-orange-600' : 'text-gray-400'}`}>
                            {estimatesChanged} Changes ({pointsChanged >= 0 ? '+' : ''}{pointsChanged} pts)
                          </p>
                          <p className={`text-xs ${hasEstimateChanges ? 'text-orange-400' : 'text-gray-400'}`}>
                            {pointsChanged === 0 ? 'No Change in Estimates' : `${pointsChanged >= 0 ? '+' : ''}${pointsChanged} in Estimates`}
                          </p>
                        </button>
                      </div>
                    </div>
                  </>
                )
              })()}
            </div>

            {/* Delivered */}
            <div className='flex items-center'>
              <div className='mr-3 text-right'>
                <p className='text-sm font-medium text-gray-900'>Delivered</p>
                <p className='text-xs text-gray-500'>End of sprint</p>
                <p className='mt-0.5 text-xs text-gray-400'>{analysis.resolvedBugs} bug{analysis.resolvedBugs !== 1 ? 's' : ''}</p>
              </div>
              <div className={`flex h-12 w-12 items-center justify-center rounded-full ${analysis.startPoints > 0 && analysis.resolvedPoints / analysis.startPoints > 0.85 ? 'bg-green-100' : 'bg-orange-100'}`}>
                <span className={`text-xl font-bold ${analysis.startPoints > 0 && analysis.resolvedPoints / analysis.startPoints > 0.85 ? 'text-green-600' : 'text-orange-600'}`}>{analysis.resolvedPoints}</span>
              </div>
            </div>
          </div>
        </div>
        {/* User breakdown section */}
        <div className='mt-6'>
          <Users
            users={sprint.users}
            analysis={analysis.byUser}
            projKey={projKeySafe}
            boardId={boardIdSafe}
            sprintId={sprintIdSafe}
          />
        </div>
      </main>

      {/* ---------- Slide-out panels ---------- */}
      <IssuesPanel
        title='Bugs'
        headerColor='bg-red-600'
        categories={bugCategories}
        sprintId={sprintIdSafe}
        addedByLookup={addedByLookup}
        isLoadingAddedBy={isLoadingAddedBy}
        isOpen={openPanel === 'bugs'}
        setIsOpen={(open) => setOpenPanel(open ? 'bugs' : null)}
      />
      <IssuesPanel
        title='Tickets'
        headerColor='bg-sky-600'
        categories={ticketCategories}
        sprintId={sprintIdSafe}
        addedByLookup={addedByLookup}
        isLoadingAddedBy={isLoadingAddedBy}
        isOpen={openPanel === 'tickets'}
        setIsOpen={(open) => setOpenPanel(open ? 'tickets' : null)}
      />
      <IssuesPanel
        title='Estimate Changes'
        headerColor='bg-orange-600'
        categories={changeCategories}
        sprintId={sprintIdSafe}
        addedByLookup={addedByLookup}
        isLoadingAddedBy={isLoadingAddedBy}
        isOpen={openPanel === 'changes'}
        setIsOpen={(open) => setOpenPanel(open ? 'changes' : null)}
      />
    </>
  )
}

export default SprintAnalysis
